import { UserRole, type UserRole as UserRoleType } from "@edlab-fms/shared";
import { useMemo, useState } from "react";
import { ApiClientError } from "../../lib/api/client";
import { useDonorsQuery } from "../../lib/api/donors";
import { groupSettingsUnitsByProgramme } from "../../lib/group-settings-units";
import {
  useCreateUserMutation,
  useSettingsUnitsQuery,
  useUpdateUserMutation,
  useUsersQuery,
  type UserAdminDto,
  type UserUnitScopeInput,
} from "../../lib/api/settings";
import { formatRoleLabel } from "../../lib/format/role";
import { Button } from "../../components/ui/Button";
import { Card } from "../../components/ui/Card";
import { EmptyState } from "../../components/ui/EmptyState";
import { LoadingSpinner } from "../../components/LoadingSpinner";
import { Modal } from "../../components/ui/Modal";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeaderCell,
  TableRow,
} from "../../components/ui/Table";

const MANAGEABLE_ROLES: UserRoleType[] = [
  UserRole.SUPER_ADMIN,
  UserRole.ACCOUNTS,
  UserRole.FINANCE_REVIEWER,
  UserRole.BOARD_MEMBER,
  UserRole.PROJECT_LEAD,
  UserRole.AUDITOR,
  UserRole.DONOR,
];

function scopeSummary(user: UserAdminDto, totalUnits: number): string {
  if (user.role === UserRole.SUPER_ADMIN || user.unitScopes.length === totalUnits) {
    return "All programmes";
  }
  if (user.unitScopes.length === 0) {
    return "No units assigned";
  }
  return `${user.unitScopes.length} assigned units`;
}

function UnitScopePicker({
  role,
  selected,
  onChange,
}: {
  role: UserRoleType;
  selected: UserUnitScopeInput[];
  onChange: (scopes: UserUnitScopeInput[]) => void;
}) {
  const unitsQuery = useSettingsUnitsQuery(true);
  const selectedIds = new Set(selected.map((scope) => scope.unitId));

  if (role === UserRole.SUPER_ADMIN) {
    return (
      <p className="text-sm text-edlab-muted">
        Super Admin has access to all programmes without explicit unit assignment.
      </p>
    );
  }

  if (unitsQuery.isLoading) {
    return <LoadingSpinner label="Loading programmes…" />;
  }

  const groups = groupSettingsUnitsByProgramme(unitsQuery.data?.units);

  function toggleUnit(unitId: string, checked: boolean) {
    if (checked) {
      onChange([...selected, { unitId, canRecordPayments: false }]);
      return;
    }
    onChange(selected.filter((scope) => scope.unitId !== unitId));
  }

  function togglePayments(unitId: string, checked: boolean) {
    onChange(
      selected.map((scope) =>
        scope.unitId === unitId ? { ...scope, canRecordPayments: checked } : scope,
      ),
    );
  }

  const allUnits = groups.flatMap((group) => group.units.filter((unit) => unit.active));

  function selectAllUnits(): void {
    onChange(
      allUnits.map((unit) => ({
        unitId: unit.id,
        canRecordPayments: selected.find((scope) => scope.unitId === unit.id)?.canRecordPayments ?? false,
      })),
    );
  }

  return (
    <div className="grid gap-3">
      <div className="flex justify-end">
        <Button type="button" variant="secondary" onClick={selectAllUnits}>
          Select all units
        </Button>
      </div>
      <div className="grid max-h-64 gap-3 overflow-y-auto rounded-lg border border-edlab-line p-3">
      {groups.map((group) => (
        <div key={group.id}>
          <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-edlab-muted">
            {group.name}
          </p>
          <div className="grid gap-2">
            {group.units.filter((unit) => unit.active).map((unit) => {
              const checked = selectedIds.has(unit.id);
              const scope = selected.find((entry) => entry.unitId === unit.id);
              return (
                <div key={unit.id} className="flex flex-wrap items-center gap-3 text-sm">
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={(e) => {
                        toggleUnit(unit.id, e.target.checked);
                      }}
                    />
                    {unit.name} ({unit.code})
                  </label>
                  {checked && role === UserRole.ACCOUNTS ? (
                    <label className="flex items-center gap-2 text-edlab-muted">
                      <input
                        type="checkbox"
                        checked={scope?.canRecordPayments ?? false}
                        onChange={(e) => {
                          togglePayments(unit.id, e.target.checked);
                        }}
                      />
                      Can record payments
                    </label>
                  ) : null}
                </div>
              );
            })}
          </div>
        </div>
      ))}
      </div>
    </div>
  );
}

function UserFormModal({
  open,
  onClose,
  user,
}: {
  open: boolean;
  onClose: () => void;
  user?: UserAdminDto;
}) {
  const isEdit = Boolean(user);
  const createUser = useCreateUserMutation();
  const updateUser = useUpdateUserMutation();
  const donors = useDonorsQuery(open);

  const [name, setName] = useState(user?.name ?? "");
  const [email, setEmail] = useState(user?.email ?? "");
  const [role, setRole] = useState<UserRoleType>(
    user ? (user.role as UserRoleType) : UserRole.PROJECT_LEAD,
  );
  const [active, setActive] = useState(user?.active ?? true);
  const [password, setPassword] = useState("");
  const [unitScopes, setUnitScopes] = useState<UserUnitScopeInput[]>(
    user?.unitScopes.map((scope) => ({
      unitId: scope.unitId,
      canRecordPayments: scope.canRecordPayments,
    })) ?? [],
  );
  const [donorId, setDonorId] = useState(user?.donorId ?? "");
  const [error, setError] = useState<string | null>(null);

  const pending = createUser.isPending || updateUser.isPending;

  async function submit() {
    setError(null);
    try {
      if (isEdit && user) {
        await updateUser.mutateAsync({
          id: user.id,
          name,
          role,
          active,
          unitScopes,
          donorId: role === UserRole.DONOR ? donorId || null : null,
          ...(password ? { password } : {}),
        });
      } else {
        await createUser.mutateAsync({
          email,
          name,
          role,
          password,
          unitScopes,
          donorId: role === UserRole.DONOR ? donorId || null : null,
        });
      }
      onClose();
    } catch (err) {
      setError(err instanceof ApiClientError ? err.message : "Could not save user.");
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={isEdit ? "Edit user access" : "Create user"}
      description="Assign a role and programme scope. Authorization is enforced server-side."
      footer={
        <>
          <Button
            variant="secondary"
            type="button"
            onClick={() => {
              onClose();
            }}
          >
            Cancel
          </Button>
          <Button disabled={pending} onClick={() => void submit()}>
            {pending ? "Saving…" : isEdit ? "Save changes" : "Create user"}
          </Button>
        </>
      }
    >
      <div className="grid gap-4">
        {error ? (
          <p className="edlab-error">
            {error}
          </p>
        ) : null}
        <label className="grid gap-1 text-sm">
          Name
          <input
            className="edlab-input"
            value={name}
            onChange={(e) => {
              setName(e.target.value);
            }}
          />
        </label>
        <label className="grid gap-1 text-sm">
          Email
          <input
            type="email"
            className="edlab-input"
            value={email}
            disabled={isEdit}
            onChange={(e) => {
              setEmail(e.target.value);
            }}
          />
        </label>
        <label className="grid gap-1 text-sm">
          Role
          <select
            className="edlab-input"
            value={role}
            onChange={(e) => {
              setRole(e.target.value as UserRoleType);
            }}
          >
            {MANAGEABLE_ROLES.map((entry) => (
              <option key={entry} value={entry}>
                {formatRoleLabel(entry)}
              </option>
            ))}
          </select>
        </label>
        {role === UserRole.DONOR ? (
          <label className="grid gap-1 text-sm">
            Linked donor profile
            <select
              className="edlab-input"
              value={donorId}
              onChange={(e) => {
                setDonorId(e.target.value);
              }}
            >
              <option value="">Select donor…</option>
              {(donors.data?.donors ?? []).map((donor) => (
                <option key={donor.id} value={donor.id}>
                  {donor.name}
                </option>
              ))}
            </select>
          </label>
        ) : null}
        {isEdit ? (
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={active}
              onChange={(e) => {
                setActive(e.target.checked);
              }}
            />
            Account active
          </label>
        ) : null}
        <label className="grid gap-1 text-sm">
          {isEdit ? "New password (optional)" : "Password"}
          <input
            type="password"
            className="edlab-input"
            value={password}
            onChange={(e) => {
              setPassword(e.target.value);
            }}
            placeholder={isEdit ? "Leave blank to keep current password" : "Min 12 characters"}
          />
        </label>
        <div className="grid gap-2">
          <span className="text-sm font-medium">Programme access</span>
          <UnitScopePicker role={role} selected={unitScopes} onChange={setUnitScopes} />
        </div>
      </div>
    </Modal>
  );
}

export function UserAdminPanel() {
  const users = useUsersQuery(true);
  const units = useSettingsUnitsQuery(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [editUser, setEditUser] = useState<UserAdminDto | null>(null);

  const totalUnits = units.data?.units.length ?? 0;
  const sortedUsers = useMemo(() => {
    return [...(users.data?.users ?? [])]
      .filter((user) => user.role !== UserRole.DONOR)
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [users.data?.users]);

  return (
    <>
      <Card>
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
          <div>
            <h2 className="text-lg font-semibold text-edlab-ink">People & access</h2>
          </div>
          <Button
            size="small"
            onClick={() => {
              setCreateOpen(true);
            }}
          >
            Add person
          </Button>
        </div>

        {users.isLoading ? (
          <LoadingSpinner label="Loading users…" />
        ) : sortedUsers.length === 0 ? (
          <EmptyState title="No users" description="Create the first user account." />
        ) : (
          <Table>
            <TableHead>
              <TableRow>
                <TableHeaderCell>Person</TableHeaderCell>
                <TableHeaderCell>Role</TableHeaderCell>
                <TableHeaderCell>Programme access</TableHeaderCell>
                <TableHeaderCell>Status</TableHeaderCell>
                <TableHeaderCell> </TableHeaderCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedUsers.map((user) => (
                <TableRow key={user.id}>
                  <TableCell>
                    <b className="block text-sm text-edlab-ink">{user.name}</b>
                    <span className="mt-1 block text-xs text-[#8b949c]">{user.email}</span>
                  </TableCell>
                  <TableCell>{formatRoleLabel(user.role as UserRoleType)}</TableCell>
                  <TableCell>{scopeSummary(user, totalUnits)}</TableCell>
                  <TableCell>{user.active ? "Active" : "Disabled"}</TableCell>
                  <TableCell>
                    <Button
                      variant="secondary"
                      size="small"
                      onClick={() => {
                        setEditUser(user);
                      }}
                    >
                      Edit access
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Card>

      {createOpen ? (
        <UserFormModal
          open
          onClose={() => {
            setCreateOpen(false);
          }}
        />
      ) : null}
      {editUser ? (
        <UserFormModal
          key={editUser.id}
          open
          user={editUser}
          onClose={() => {
            setEditUser(null);
          }}
        />
      ) : null}
    </>
  );
}
